import { useCallback, useMemo, useState } from "react"
import { useForms } from "./useForm"
import { readCookie } from "./useCookieField"

// Petit hash synchrone (FNV-1a) répété pour produire 32 caractères hex, style md5
function fnv1a32Hex(str) {
  let hash = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i) & 0xff
    // 32-bit FNV prime: 16777619 => (hash * 16777619) mod 2^32
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0
  }
  return ("00000000" + hash.toString(16)).slice(-8)
}

function makeAlphaTokenMd5Like(input) {
  // Concatène et sale la chaîne, puis concatène 4 passes FNV-1a pour 32 chars hex
  const saltBase = `${input}`
  const p0 = fnv1a32Hex(saltBase + "|0")
  const p1 = fnv1a32Hex(saltBase + "|1")
  const p2 = fnv1a32Hex(saltBase + "|2")
  const p3 = fnv1a32Hex(saltBase + "|3")
  return `${p0}${p1}${p2}${p3}`
}

// sessionStorage helpers (safe côté SSR / privacy modes)
function getSessionStorage() {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) return window.sessionStorage
  } catch {
    // Ignore access errors (Safari private, etc.)
  }
  return null
}

function getSubmissionStorageKey(formId) {
  return `_submissionId_${formId ?? 'unknown'}`
}

// Extrait les données UTM depuis un cookie (par défaut: _ionis_user_data)
// On ne fait pas de setValue ici, on renvoie la donnée directement pour le payload
function extractUtmFromCookie(cookieName = "_ionis_user_data") {
  const raw = readCookie(cookieName)
  if (!raw) return null
  try {
    // La valeur peut être encodée; on essaye de décoder puis parser JSON si possible
    const decoded = decodeURIComponent(raw)
    try {
      return JSON.parse(decoded)
    } catch {
      return decoded
    }
  } catch {
    return raw
  }
}

// Normalise l'objet UTM pour produire les champs attendus par Salesforce
// Les champs non renseignés ne sont pas envoyés (pas de clé dans l'objet)
function normalizeUtm(utmRaw) {
  // Accepte soit l'objet complet du cookie, soit directement l'objet utm
  const sourceObj = (utmRaw && typeof utmRaw === 'object' && utmRaw.utm && typeof utmRaw.utm === 'object')
    ? utmRaw.utm
    : (utmRaw && typeof utmRaw === 'object')
      ? utmRaw
      : null

  const out = {}

  const setIfString = (key, val) => {
    if (val === undefined || val === null) return
    const first = Array.isArray(val) ? (val[0] ?? "") : val
    const str = String(first)
    if (str !== "") out[key] = str
  }

  if (sourceObj && typeof sourceObj === 'object') {
    setIfString("InputUtm_campaign", sourceObj.campaign)
    setIfString("InputUtm_content", sourceObj.content)
    setIfString("InputUtm_id", sourceObj.id)
    setIfString("InputUtm_medium", sourceObj.medium)
    setIfString("InputUtm_source", sourceObj.source)
    setIfString("InputUtm_term", sourceObj.term)
    if (sourceObj.ionis === true) out.InputUtm_ionis = true
  }

  return out
}

// Hook principal
// submission(payload) va construire l'objet attendu et appeler salesforce.flowInterview
export function useSubmission() {
  const { salesforce, setError } = useForms()

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setLocalError] = useState(null)

  const getPageMeta = useCallback(() => {
    const pageUrl = typeof window !== "undefined" ? window.location.href : null
    // document.referrer peut être vide côté SPA si navigation interne
    const referrerUrl = typeof document !== "undefined" ? (document.referrer || null) : null
    return { pageUrl, referrerUrl }
  }, [])

  const buildPayload = useCallback((input) => {
    const { pageUrl, referrerUrl } = getPageMeta()
    // Permettre de forcer un nom de cookie différent via input.utmCookieName si souhaité
    const utm = input?.utm ?? extractUtmFromCookie(input?.utmCookieName)

    // Mapping UTM normalisé pour le payload
    const utmMapped = normalizeUtm(utm)

    // Champs temporels et identifiant de soumission
    // Utiliser un ISO string pour compatibilité Salesforce
    const submittedAt = new Date().toISOString()
    const lead = input?.lead ?? null
    // consentTimestamp autrefois calculé; non utilisé dans le payload actuel

  // Préparer données d'identification
  const email = lead?.Email || lead?.email || ''
  const formId = input?.formId ?? null

    const base = {
      InputCampaignStatus: (input?.campaignStatus ?? ""),
      InputFormType: formId,
      InputLeadToCreate: lead,
      InputPageUrl: pageUrl,
      InputReferrerUrl: (referrerUrl ?? ""),
      InputSubmittedAt: submittedAt,
    }

    // Champs optionnels: ajouter uniquement s'ils sont renseignés
    if (input?.campaignId !== undefined && input.campaignId !== "") {
      base.InputCampaignId = input.campaignId
    }
    if (input?.slotId !== undefined && input.slotId !== "") {
      base.InputEventSlot = input.slotId
    }
    if (input?.places !== undefined && input.places !== null) {
      base.InputNumberOfPerson = input.places
    }
    if (input?.description !== undefined && input.description !== "") {
      base.InputTaskDescription = input.description
    }
    if (input?.subject !== undefined && input.subject !== "") {
      base.InputTaskSubject = input.subject
    }

    // UTM: ne plus injecter les champs dans les inputs (traité côté back)

    // rawPayload contient l’ensemble des données construites précédemment (sans submissionId)
    const rawPayload = { ...base }

    // submissionId persistant dans la session: réutiliser si présent, sinon générer et stocker
    const ss = getSessionStorage()
    const ssKey = getSubmissionStorageKey(formId)
    let submissionId = null
    try {
      submissionId = ss?.getItem(ssKey) || null
    } catch {
      submissionId = null
    }
    if (!submissionId) {
      // Token alphanumérique (style md5): hash de formId + email + rawPayload
      submissionId = makeAlphaTokenMd5Like([
        formId,
        email,
        JSON.stringify(rawPayload)
      ].filter(Boolean).join('#'))
      try { ss?.setItem(ssKey, submissionId) } catch { /* ignore */ }
    }

    // Injecter submissionId dans base et rawPayload
    base.InputSubmissionId = submissionId
    rawPayload.InputSubmissionId = submissionId

    return {
      ...base,
      name: "Ingestion_Soumission_Attribution",
      InputRawPayload: JSON.stringify(rawPayload)
    }
  }, [getPageMeta])

  const submission = useCallback(async (input) => {
    if (!salesforce) {
      const err = new Error("Salesforce n'est pas initialisé")
      setLocalError(err)
      if (setError) setError("Une erreur est survenue lors de l'envoi", err.message)
      throw err
    }

    setLoading(true)
    setLocalError(null)
    try {
      const payload = buildPayload(input)
      const res = await salesforce.flowInterview(payload)
      setResult(res)
      // Soumission réussie: supprimer l'id de session pour permettre une nouvelle soumission future
      try {
        const ss = getSessionStorage()
        const ssKey = getSubmissionStorageKey(input?.formId ?? null)
        ss?.removeItem(ssKey)
      } catch { /* ignore */ }
      return res
    } catch (e) {
      setLocalError(e)
      if (setError) setError("Une erreur est survenue lors de l'envoi", e?.message || String(e))
      throw e
    } finally {
      setLoading(false)
    }
  }, [buildPayload, salesforce, setError])

  return useMemo(() => ({ submission, loading, result, error }), [submission, loading, result, error])
}

export default useSubmission
